import { Component, ElementRef, HostListener, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiResponse } from '../../../_models/api-response.model';
import { NotificationService } from '../../../_services/app/notification.service';
import { MenuStateService } from '../../../_services/app/menu-state.service';
import { 
  ReservationDetail, 
  OfferWithActions,
  ReservationActionState,
  CustomerOffer
} from '../../../_models/reservation.model';
import { OfferUtilsService } from '../../../_utils/offer-utils.service';
import { ClientReservationService } from '../../../_services/api/reservation-client.service';
import { ReservationActionsUtilsService } from '../../../_utils/reservation-actions-utils.service';
import { MemberReservationOfferCard } from '../member-reservation-offer-card/member-reservation-offer-card';


@Component({
  selector: 'app-member-reservation-details',
  imports: [CommonModule, FormsModule, MemberReservationOfferCard],
  templateUrl: './member-reservation-details.html',
  styleUrl: './member-reservation-details.scss',
})
export class MemberReservationDetails implements OnInit {
  reservationId!: number;
  reservation: ReservationDetail | null = null;
  offersWithActions: OfferWithActions[] = [];
  isLoading = false;
  errorMessage: string | null = null;
  
  // reservation action state
  reservationActionState!: ReservationActionState;
  isPerformingAction = false;
  actionType: 'accept' | 'reject' | 'request_new' | 'cancel' | 'delete' | 'see_feedback' | null = null;
  
  // modals
  menuOpen = false;
  showDeleteModal = false;
  showCancelModal = false;
  showRejectModal = false;
  
  // offer actions
  selectedOfferId: number | null = null;
  pendingOffer: CustomerOffer | null = null;
  
  private subscriptions: Subscription[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private reservationService: ClientReservationService,
    private offerUtils: OfferUtilsService,
    private reservationActionsUtils: ReservationActionsUtilsService,
    private notification: NotificationService,
    private elementRef: ElementRef, 
    private menuStateService: MenuStateService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit(): void {    
    this.loadReservationId();
    if (this.reservationId) {
      this.menuStateService.activeMenu$.subscribe(activeId => {
        this.menuOpen = activeId === this.reservationId;
      });
      if (isPlatformBrowser(this.platformId)) {
        document.addEventListener('click', this.closeMenu.bind(this));
      }
    }
  }

  ngOnDestroy() {
    if (isPlatformBrowser(this.platformId)) {
      document.removeEventListener('click', this.closeMenu.bind(this));
    }
  }

  private loadReservationId(): void {
    const routeSub = this.route.paramMap.subscribe(params => {
      const id = params.get('id');
      
      if (id) {
        this.reservationId = +id;
        this.loadReservationDetails();
      } else {
        this.errorMessage = 'ID de réservation non trouvé';
        this.goToList();
      }
    });
    this.subscriptions.push(routeSub);
  }

  private loadReservationDetails(): void {
    this.isLoading = true;
    this.errorMessage = null;

    const detailsSub = this.reservationService.getReservationDetails(this.reservationId).subscribe({
      next: (response: ApiResponse<ReservationDetail>) => {
        if (response.success && response.data) {
          this.reservation = response.data;
          this.updateReservationActionState();
          this.prepareOffers();
        } else {
          this.errorMessage = response.message || 'Erreur lors du chargement des données';
        }
        setTimeout(() => {
          this.isLoading = false; 
          if (isPlatformBrowser(this.platformId)) {
            window.scrollTo({top: 0, behavior: 'smooth'});
          }
        }, 300);        
      },
      error: (error) => {
        console.error('Error loading reservation details:', error);
        this.errorMessage = 'Erreur de chargement des détails du reservation';
        this.isLoading = false;
        
        if (error.status === 404) {
          this.errorMessage = 'Réservation non trouvée';
          setTimeout(() => this.goToList(), 2000);
        }
      }
    });
    this.subscriptions.push(detailsSub);
  }

  // update the reservation action state
  private updateReservationActionState(): void {
    if (!this.reservation) return;
    const reservationForActions = {
      id: this.reservation.id,
      eventName: this.reservation.eventName,
      reservationEventType: this.reservation.reservationEventType,
      requiredAgents: this.reservation.requiredAgents,
      startDateTime: this.reservation.startDateTime,
      endDateTime: this.reservation.endDateTime,
      address: this.reservation.address,
      reservationClientStatus: this.reservation.reservationClientStatus,
      reservationAdminStatus: this.reservation.reservationAdminStatus,
      createdAt: this.reservation.createdAt,
      serviceFeedbackId: this.reservation.serviceFeedbackId,
      offers: this.reservation.offers?.map(offer => ({
        id: offer.id,
        price: offer.price,
        description: offer.description,
        status: offer.status,
        createdAt: offer.createdAt
      })) || []
    };

    this.reservationActionState = this.reservationActionsUtils.getReservationActionState(
      reservationForActions as any
    );

    // get the first pending offer for accept/reject actions
    this.pendingOffer = this.reservationActionsUtils.getFirstPendingOffer(
      reservationForActions as any
    );
    if (this.pendingOffer) {
      this.selectedOfferId = this.pendingOffer.id;
    }
  }

  // reservation action methods
  acceptOffer(): void {
    if (!this.selectedOfferId) {
      this.notification.showError('Aucune offre en attente à accepter');
      return;
    }

    this.actionType = 'accept';
    this.menuOpen = false;
    this.isPerformingAction = true;

    this.reservationService.acceptOffer(this.reservationId, this.selectedOfferId)
      .subscribe({
        next: (response) => {
          this.notification.showSuccess(/*response.message || */'Offre acceptée avec succès');
          this.loadReservationDetails();
          this.isPerformingAction = false;
          this.actionType = null;
        },
        error: (error) => {
          this.notification.showError(error.error?.message || 'Erreur lors de l\'acceptation de l\'offre');
          this.isPerformingAction = false;
          this.actionType = null;
        }
      });
  }

  rejectOffer(): void {
    if (!this.selectedOfferId) {
      this.notification.showError('Aucune offre en attente à refuser');
      return;
    }

    this.actionType = 'reject';
    this.menuOpen = false;
    this.showRejectModal = true;
  }

  confirmRejectOffer(): void {
    if (!this.selectedOfferId) return;

    this.isPerformingAction = true;
    this.showRejectModal = false;

    this.reservationService.rejectOffer(this.reservationId, this.selectedOfferId)
      .subscribe({
        next: (response) => {
          this.notification.showSuccess(/*response.message || */'Offre refusée avec succès');
          this.loadReservationDetails();
          this.isPerformingAction = false;
          this.actionType = null;
        },
        error: (error) => {
          this.notification.showError(error.error?.message || 'Erreur lors du refus de l\'offre');
          this.isPerformingAction = false;
          this.actionType = null;
        }
      });
  }

  requestNewOffer(): void {
    this.actionType = 'request_new';
    this.menuOpen = false;
    this.isPerformingAction = true;

    this.reservationService.requestNewOffer(this.reservationId)
      .subscribe({
        next: (response) => {
          this.notification.showSuccess(/*response.message || */'Nouvelle offre demandée avec succès');
          this.loadReservationDetails();
          this.isPerformingAction = false;
          this.actionType = null;
        },
        error: (error) => {
          this.notification.showError(error.error?.message || 'Erreur lors de la demande de nouvelle offre');
          this.isPerformingAction = false;
          this.actionType = null;
        }
      });
  }

  updateReservation(): void {
    this.menuOpen = false;
    this.router.navigate(['/member/reservations/edit', this.reservationId]);
  }

  cancelReservation(): void {
    this.actionType = 'cancel';
    this.menuOpen = false;
    this.showCancelModal = true;
  }

  confirmCancelReservation(): void {
    this.isPerformingAction = true;
    this.showCancelModal = false;

    this.reservationService.cancelReservation(this.reservationId)
      .subscribe({
        next: (response) => {
          this.notification.showSuccess(/*response.message || */'Réservation annulée avec succès');
          this.goToList();
          this.isPerformingAction = false;
          this.actionType = null;
        },
        error: (error) => {
          this.notification.showError(error.error?.message || 'Erreur lors de l\'annulation de la réservation');
          this.isPerformingAction = false;
          this.actionType = null;
        }
      });
  }

  deleteReservation(): void {
    this.actionType = 'delete';
    this.menuOpen = false;
    this.showDeleteModal = true;
  }

  confirmDeleteReservation(): void {
    this.isPerformingAction = true;
    this.showDeleteModal = false;

    this.reservationService.deleteReservation(this.reservationId)
      .subscribe({
        next: (response) => {
          this.notification.showSuccess(/*response.message || */'Réservation supprimée avec succès');
          this.goToList();
          this.isPerformingAction = false;
          this.actionType = null;
        },
        error: (error) => {
          this.notification.showError(error.error?.message || 'Erreur lors de la suppression de la réservation');
          this.isPerformingAction = false;
          this.actionType = null;
        }
      });
  }

  // Modal close methods
  closeDeleteModal(): void {
    this.showDeleteModal = false;
    this.actionType = null;
  }

  closeCancelModal(): void {
    this.showCancelModal = false;
    this.actionType = null;
  }

  closeRejectModal(): void {
    this.showRejectModal = false;
    this.actionType = null;
  }

  // Offer card event handlers
  onOfferAccepted(): void {
    this.loadReservationDetails();
  }

  onOfferRejected(): void {
    this.loadReservationDetails();
  }

  // Helper methods
  toggleMenu(event: MouseEvent) {
    event.stopPropagation();
    if (this.menuOpen) {
      this.menuStateService.setActiveMenu(null);
    } else {
      this.menuStateService.setActiveMenu(this.reservationId);
    }
  }

  @HostListener('document:click', ['$event'])
  closeMenu(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.menuOpen = false;
    }
    if (this.menuOpen) {
      this.menuStateService.setActiveMenu(null);
    }
  }

  goToList(): void {
    this.router.navigate(['/member/reservations']);
  }

  private prepareOffers(): void {
    if (!this.reservation || !this.reservation.offers) {
      this.offersWithActions = [];
      return;
    }

    this.offersWithActions = this.offerUtils.getOffersWithActions(this.reservation.offers);
    this.offersWithActions = this.offerUtils.sortOffersByStatus(this.offersWithActions);
  }

  // Reservation status helpers
  getReservationAdminStatusClass(): string {
    if (!this.reservation) return '';
    return this.offerUtils.getReservationOverallStatusClass(
      this.reservation.reservationClientStatus, 
      this.reservation.reservationAdminStatus
    );
  }

  getReservationAdminStatusText(): string {
    if (!this.reservation) return '';
    return this.offerUtils.getReservationOverallStatusText(
      this.reservation.reservationClientStatus, 
      this.reservation.reservationAdminStatus
    );
  }

  // Action button helpers
  getActionButtonText(action: string): string {
    return this.reservationActionsUtils.getActionButtonText(action);
  }

  getLoadingText(): string {
    switch (this.actionType) {
      case 'accept': return 'Acceptation en cours...';
      case 'reject': return 'Refus en cours...';
      case 'request_new': return 'Demande en cours...';
      case 'cancel': return 'Annulation en cours...';
      case 'delete': return 'Suppression en cours...';
      default: return 'Traitement en cours...';
    }
  }

  formatAddress(address: any): string {
    if (!address) return 'Non spécifiée';
    
    const parts: string[] = [];
    
    if (address.streetNumber && address.street) {
      parts.push(`${address.street} ${address.streetNumber}`);
    } else if (address.street) {
      parts.push(address.street);
    }
    
    if (address.postalCode && address.city) {
      parts.push(`${address.postalCode} ${address.city}`);
    } else if (address.postalCode) {
      parts.push(address.postalCode);
    } else if (address.city) {
      parts.push(address.city);
    }
    
    if (parts.length === 0) {
      return 'Non spécifiée';
    }
    
    return parts.join(', ') + ".";
  }

  giveFeedback(): void {
    if (this.reservation?.id) {
      this.router.navigate(['/member/reservations', this.reservation.id, 'feedback']);
    }
  }

   feedbackDetails(){
    this.menuOpen = false;
    this.router.navigate(['/member/reservations', this.reservation!.id, 'feedback', 'details']);
  }
}